/**
 * Charger une ressource, avec ses trois issues.
 *
 * Un `useEffect` qui pose un état après une requête est le défaut le plus
 * courant d'une interface React : le composant se démonte, la réponse arrive,
 * et React se plaint — ou pire, une réponse lente écrase une réponse rapide
 * partie après elle. La sentinelle `vivant` traite le premier cas ; le
 * compteur de génération traite le second.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export interface Chargeur<T> {
  donnees: T | null;
  erreur: unknown;
  charge: boolean;
  recharger: () => void;
}

export function useDonnees<T>(
  obtenir: () => Promise<T>,
  dependances: unknown[],
): Chargeur<T> {
  const [donnees, poser] = useState<T | null>(null);
  const [erreur, poserErreur] = useState<unknown>(null);
  const [charge, poserCharge] = useState(true);
  const [tour, relancer] = useState(0);
  const generation = useRef(0);
  //: Le reflet courant de `donnees`, lu dans le `catch` sans piège de closure.
  const dernieres = useRef<T | null>(null);

  // **Une nouvelle ressource repart à vide.** `dernieres` sert à ne pas vider
  // l'écran sur une relecture ratée ; mais quand les dépendances changent, ce
  // qu'on tient appartient à l'ancienne ressource, et un premier chargement
  // raté doit redevenir fatal. On le remet à zéro **au rendu**, avant l'effet,
  // pour que le `catch` d'un chargement neuf ne voie pas l'ancienne donnée.
  const signature = JSON.stringify(dependances);
  const signaturePrecedente = useRef(signature);
  if (signaturePrecedente.current !== signature) {
    signaturePrecedente.current = signature;
    dernieres.current = null;
  }

  useEffect(() => {
    let vivant = true;
    const mien = ++generation.current;
    poserCharge(true);
    poserErreur(null);

    obtenir()
      .then((issue) => {
        // Deux gardes, deux dangers différents : `vivant` pour le composant
        // démonté, `mien` pour la réponse arrivée dans le désordre.
        if (vivant && mien === generation.current) {
          dernieres.current = issue;
          poser(issue);
          poserErreur(null);
        }
      })
      .catch((exc) => {
        // **On ne vide pas un écran qui a déjà des données.** Une relecture de
        // fond qui rate — souvent juste après une action réussie — ne doit pas
        // basculer l'onglet en carte d'erreur et faire croire le geste échoué.
        // L'erreur ne devient fatale que sans rien à montrer ; sinon les
        // données restent, et la prochaine relecture réussie les rafraîchira.
        if (vivant && mien === generation.current && dernieres.current == null) {
          poserErreur(exc);
        }
      })
      .finally(() => {
        if (vivant && mien === generation.current) poserCharge(false);
      });

    return () => {
      vivant = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependances, tour]);

  const recharger = useCallback(() => relancer((n) => n + 1), []);
  return { donnees, erreur, charge, recharger };
}

/**
 * Relance périodique — pour ce qui bouge sans qu'on y touche : les travaux
 * d'import, l'avancement d'une préparation.
 *
 * **Elle s'arrête quand l'onglet passe en arrière-plan.** Un tiroir laissé
 * ouvert une nuit interrogerait le portail dix mille fois pour personne.
 */
export function useRelance(action: () => void, intervalle: number, actif = true) {
  const dernier = useRef(action);
  dernier.current = action;

  useEffect(() => {
    if (!actif) return;
    const battre = () => {
      if (document.visibilityState === "visible") dernier.current();
    };
    const minuteur = window.setInterval(battre, intervalle);
    document.addEventListener("visibilitychange", battre);
    return () => {
      window.clearInterval(minuteur);
      document.removeEventListener("visibilitychange", battre);
    };
  }, [intervalle, actif]);
}

/** Les états qu'un travail traverse **avant** d'être fini. */
export const TRAVAIL_EN_COURS = new Set([
  "QUEUED", "DOWNLOADING", "VALIDATING", "SCANNING",
  "STORING", "MATERIALIZING", "PROCESSING",
]);

export interface Veille<T> {
  travaux: T[];
  enCours: number;
  /** Ce qui change quand la file change. **Un écran s'y accroche** pour
   *  relire ses propres données au bon moment, sans savoir ce qu'est un
   *  travail ni comment il progresse. */
  empreinte: string;
  charge: boolean;
  erreur: unknown;
  rafraichir: () => void;
}

/**
 * Ce que les ouvriers sont en train de changer sur cette affaire.
 *
 * **Le portail ne relisait jamais ce qu'un ouvrier modifie.** Chaque écran
 * chargeait une fois et n'y revenait plus : une préparation lancée passait
 * `DRAFT → PROCESSING → READY` en base pendant que l'onglet affichait encore
 * « Brouillon », et le métreur n'avait d'autre recours que de recharger la
 * page. Le tiroir des traitements était le seul à se relancer — le mécanisme
 * existait, il n'était branché qu'à un endroit.
 *
 * **Une seule veille pour toute l'affaire**, tenue par la page et prêtée à ses
 * onglets. Deux relances sur le même point d'accès — l'une pour le tiroir,
 * l'autre pour l'écran — auraient doublé les requêtes pour la même réponse.
 *
 * **Deux cadences, et c'est ce qui rend la chose acceptable.** Trois secondes
 * quand quelque chose tourne, quinze quand la file dort. Une file au repos n'a
 * rien de plus à dire dans trois secondes qu'elle n'en dit maintenant ; mais
 * ne plus l'interroger du tout ferait manquer ce qu'un collègue, un collecteur
 * ou un autre onglet vient d'y déposer. Et tout s'arrête quand l'onglet passe
 * en arrière-plan.
 */
export function useVeille<T extends { job_id: string; etat: string }>(
  affaireId: string,
  lire: (chemin: string) => Promise<{ donnees: T[] }>,
): Veille<T> {
  const travaux = useDonnees<{ donnees: T[] }>(
    () => lire(`/api/v1/affaires/${affaireId}/traitements`),
    [affaireId],
  );

  const liste = travaux.donnees?.donnees ?? [];
  const enCours = liste.filter((t) => TRAVAIL_EN_COURS.has(t.etat)).length;
  useRelance(travaux.recharger, enCours ? 3000 : 15000);

  // L'identité **et** l'état de chaque travail : un import qui passe de
  // `QUEUED` à `READY` sans en changer le nombre doit réveiller les écrans.
  const empreinte = liste.map((t) => `${t.job_id}:${t.etat}`).join("|");

  return {
    travaux: liste, enCours, empreinte,
    charge: travaux.charge, erreur: travaux.erreur,
    rafraichir: travaux.recharger,
  };
}

/**
 * Relit une ressource **dès que la file bouge**.
 *
 * L'écran n'a pas à savoir ce qu'est un travail : il dit « quand la file
 * change, relis-moi ». Le premier rendu est ignoré — la ressource vient
 * d'être chargée, et la relire aussitôt doublerait chaque ouverture d'onglet.
 */
export function useSuivreLaFile(empreinte: string, relire: () => void) {
  const premier = useRef(true);
  const dernier = useRef(relire);
  dernier.current = relire;

  useEffect(() => {
    if (premier.current) {
      premier.current = false;
      return;
    }
    dernier.current();
  }, [empreinte]);
}
